'use client';

import { Component, ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Typography } from '@/primitives';
import { slate } from '@/lib/colours';

interface CriticalErrorBoundaryProps {
  children: ReactNode;
}

interface CriticalErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * A minimal error fallback for critical errors that occur at the provider level
 */
function CriticalErrorFallback({ error }: { error?: Error }) {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <html lang="en">
      <body>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '32px',
            textAlign: 'center',
            backgroundColor: slate[50],
          }}
        >
          <Box
            sx={{
              maxWidth: '500px',
              padding: '32px',
              borderRadius: '12px',
              backgroundColor: 'white',
              border: `1px solid ${slate[200]}`,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography
              variant="headlinelarge"
              sx={{
                color: slate[900],
                marginBottom: '16px',
                fontWeight: 600,
              }}
            >
              Application Error
            </Typography>

            <Typography
              sx={{
                color: slate[600],
                marginBottom: '24px',
                lineHeight: 1.6,
              }}
            >
              A critical error occurred while loading the application. Please
              try reloading the page.
            </Typography>

            {process.env.NODE_ENV === 'development' && error && (
              <Box
                sx={{
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: slate[100],
                  borderRadius: '8px',
                  border: `1px solid ${slate[300]}`,
                  textAlign: 'left',
                }}
              >
                <Typography
                  sx={{
                    color: slate[700],
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    wordBreak: 'break-word',
                  }}
                >
                  {error.message}
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              onClick={handleReload}
              sx={{
                backgroundColor: slate[700],
                '&:hover': {
                  backgroundColor: slate[800],
                },
              }}
            >
              Reload Page
            </Button>
          </Box>
        </Box>
      </body>
    </html>
  );
}

/**
 * ErrorBoundary for critical errors that occur at the provider level
 * This provides a minimal fallback that doesn't depend on the app's theme or providers
 */
export class CriticalErrorBoundary extends Component<
  CriticalErrorBoundaryProps,
  CriticalErrorBoundaryState
> {
  state: CriticalErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): CriticalErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Critical error caught by boundary:', error, errorInfo);

    // Try to log to Datadog if available, but don't throw if it fails
    try {
      if (typeof window !== 'undefined' && (window as any).DD_RUM) {
        (window as any).DD_RUM.addError(error, errorInfo);
      }
    } catch {
      // Ignore errors when logging to Datadog
    }
  }

  render() {
    if (this.state.hasError) {
      return <CriticalErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}
